import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  MapPin,
  Building2,
  ArrowRight,
  Send,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

/**
 * Contact page — public, static, professional.
 *
 * The form is intentionally NOT wired to a backend yet. Submitting
 * shows a clear, intent-driven notice that says the form is being
 * finalised and gives the visitor the direct email address as a
 * working fallback. No silent failures, no fake success states.
 */
export default function ContactPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" data-testid="contact-page">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-blue-100 text-blue-800 hover:bg-blue-200">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Get in touch
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            We'd love to <span className="text-blue-600">hear from you</span>.
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Whether you're an applicant, a hiring manager, or a partner who sees what we're
            building — reach out. We read every message.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Contact details column */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="border-blue-100">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 bg-blue-50 rounded-lg flex-shrink-0">
                      <Mail className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 mb-1">Email</p>
                      <p
                        className="text-gray-600 break-words"
                        data-testid="contact-email-placeholder"
                      >
                        hello@inter-view.ca
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Replace with your preferred public address.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-blue-100">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 bg-blue-50 rounded-lg flex-shrink-0">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Business</p>
                      <p className="text-gray-600">
                        Inter-View · A Canadian product
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Replace with your registered business details when ready.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-blue-100">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 bg-blue-50 rounded-lg flex-shrink-0">
                      <MapPin className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Based in</p>
                      <p className="text-gray-600">Canada 🇨🇦</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Add your city / region when you're ready to share it.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Form column */}
            <div className="lg:col-span-3">
              <Card className="border-blue-100 shadow-sm">
                <CardContent className="p-6 sm:p-8">
                  {formSubmitted ? (
                    <div
                      className="text-center py-10"
                      data-testid="contact-form-submitted"
                    >
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 mb-4">
                        <Mail className="h-7 w-7 text-blue-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Thanks for reaching out.
                      </h3>
                      <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
                        Our contact form is being finalised. In the meantime, please email us
                        directly at{" "}
                        <a
                          href="mailto:hello@inter-view.ca"
                          className="text-blue-600 hover:text-blue-700 font-semibold"
                        >
                          hello@inter-view.ca
                        </a>{" "}
                        and we'll get back to you within one business day.
                      </p>
                      <Button
                        variant="outline"
                        className="mt-6"
                        onClick={() => setFormSubmitted(false)}
                      >
                        Send another message
                      </Button>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit}
                      className="space-y-4"
                      data-testid="contact-form"
                      noValidate
                    >
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="contact-name">Your name</Label>
                          <Input
                            id="contact-name"
                            name="name"
                            placeholder="Jane Smith"
                            required
                            data-testid="contact-form-name"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="contact-email">Email</Label>
                          <Input
                            id="contact-email"
                            name="email"
                            type="email"
                            placeholder="you@example.com"
                            required
                            data-testid="contact-form-email"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="contact-subject">Subject</Label>
                        <Input
                          id="contact-subject"
                          name="subject"
                          placeholder="What's this about?"
                          data-testid="contact-form-subject"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="contact-message">Message</Label>
                        <Textarea
                          id="contact-message"
                          name="message"
                          rows={6}
                          placeholder="Tell us a bit more — questions, partnerships, feedback…"
                          required
                          data-testid="contact-form-message"
                        />
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <p className="text-xs text-gray-500">
                          We typically respond within one business day.
                        </p>
                        <Button
                          type="submit"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="contact-form-submit"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send message
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Not here for support? Try the product.
          </h2>
          <p className="text-lg text-blue-100 mb-8 leading-relaxed">
            The fastest way to see what Inter-View does is to build your own application
            package — it takes about ten minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-white text-blue-700 hover:bg-blue-50 text-base px-8 shadow-lg"
                data-testid="contact-cta-signup"
              >
                Get started free
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/60 text-white hover:bg-white/10 text-base px-8"
              >
                See how it works
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
